import { getAnonSessionId } from '@/data/api/anonSession'
import { getAccessToken } from '@/data/api/token'
import { getBaseApiURL } from '@/utils/env'

export type TStreamConversationPromptParams = {
  conversationId: string
  content: string
  onChunk: (chunk: string) => void
  onEmergency?: () => void
  onDangerCheck?: () => void
  signal?: AbortSignal
}

export type TStreamConversationResumeParams = {
  conversationId: string
  onChunk: (chunk: string) => void
  onEmergency?: () => void
  onDangerCheck?: () => void
  signal?: AbortSignal
}

const sleep = (ms: number) => new Promise((resolve) => setTimeout(resolve, ms))

const parseSseErrorDetail = async (response: Response): Promise<string> => {
  try {
    const payload = (await response.json()) as { detail?: unknown; message?: unknown }
    if (typeof payload.detail === 'string') {
      return payload.detail
    }
    if (typeof payload.message === 'string') {
      return payload.message
    }
  } catch {
    // ignore non-JSON error bodies
  }

  return `Request failed with status ${response.status}`
}

const emitTextProgressively = async (text: string, onChunk: (chunk: string) => void): Promise<void> => {
  const chunkSize = 24
  for (let index = 0; index < text.length; index += chunkSize) {
    onChunk(text.slice(index, index + chunkSize))
    await sleep(16)
  }
}

/** Legacy dev/prod API returns full ConversationResponse JSON instead of SSE. */
const consumeJsonPromptResponse = async (response: Response, onChunk: (chunk: string) => void): Promise<void> => {
  const conversation = (await response.json()) as {
    messages?: Array<{ role?: string; content?: string | null }>
  }
  const lastAssistant = [...(conversation.messages ?? [])].reverse().find((message) => message.role === 'assistant')
  const text = lastAssistant?.content ?? ''

  if (!text) {
    return
  }

  await emitTextProgressively(text, onChunk)
}

const parseSseEventData = (event: string): string | null => {
  const dataLines: string[] = []

  for (const rawLine of event.split('\n')) {
    if (rawLine.startsWith(':')) {
      continue
    }

    if (rawLine.startsWith('data:')) {
      dataLines.push(rawLine.slice(5).replace(/^\s/, ''))
      continue
    }

    // Misframed continuation when a chunk contained raw newlines without `data:` prefixes.
    if (dataLines.length > 0 && !/^[A-Za-z0-9_-]+:/.test(rawLine)) {
      dataLines[dataLines.length - 1] = `${dataLines[dataLines.length - 1]}\n${rawLine}`
    }
  }

  if (dataLines.length === 0) {
    return null
  }

  return dataLines.join('\n')
}

const consumeSsePromptResponse = async (
  response: Response,
  onChunk: (chunk: string) => void,
  onEmergency?: () => void,
  onDangerCheck?: () => void
): Promise<void> => {
  if (!response.body) {
    throw new Error('Streaming response body is missing')
  }

  const reader = response.body.getReader()
  const decoder = new TextDecoder()
  let buffer = ''

  while (true) {
    const { done, value } = await reader.read()
    if (done) {
      break
    }

    buffer += decoder.decode(value, { stream: true })
    const events = buffer.split('\n\n')
    buffer = events.pop() ?? ''

    for (const event of events) {
      const data = parseSseEventData(event)
      if (data === null) {
        continue
      }

      if (data === '[DONE]') {
        return
      }

      if (data === '[EMERGENCY]') {
        onEmergency?.()
        continue
      }

      if (data === '[DANGER_CHECK]') {
        onDangerCheck?.()
        continue
      }

      if (data.startsWith('[ERROR]')) {
        throw new Error(data.slice('[ERROR]'.length).trim() || 'Streaming response failed')
      }

      if (data) {
        onChunk(data)
        await sleep(0)
      }
    }
  }
}

const consumePromptResponse = async (
  response: Response,
  onChunk: (chunk: string) => void,
  onEmergency?: () => void,
  onDangerCheck?: () => void
): Promise<void> => {
  if (!response.ok) {
    throw new Error(await parseSseErrorDetail(response))
  }

  const contentType = response.headers.get('content-type') ?? ''
  if (contentType.includes('application/json')) {
    await consumeJsonPromptResponse(response, onChunk)
    return
  }

  await consumeSsePromptResponse(response, onChunk, onEmergency, onDangerCheck)
}

/**
 * POST /conversations/{id}/prompt and consume SSE text chunks.
 * Events: `data: <chunk>`, `data: [DANGER_CHECK]`, `data: [EMERGENCY]`, `data: [DONE]`, `data: [ERROR] <message>`
 */
export const streamConversationPrompt = async ({
  conversationId,
  content,
  onChunk,
  onEmergency,
  onDangerCheck,
  signal,
}: TStreamConversationPromptParams): Promise<void> => {
  const accessToken = getAccessToken()
  if (!accessToken) {
    throw new Error('Missing access token')
  }

  const response = await fetch(`${getBaseApiURL()}/conversations/${conversationId}/prompt`, {
    method: 'POST',
    headers: {
      Accept: 'text/event-stream, application/json',
      Authorization: `Bearer ${accessToken}`,
      'Content-Type': 'application/json',
    },
    body: JSON.stringify({ content }),
    signal,
  })

  await consumePromptResponse(response, onChunk, onEmergency, onDangerCheck)
}

/**
 * POST /conversations/{id}/prompt/resume after answering No to the danger check.
 */
export const streamConversationPromptResume = async ({
  conversationId,
  onChunk,
  onEmergency,
  onDangerCheck,
  signal,
}: TStreamConversationResumeParams): Promise<void> => {
  const accessToken = getAccessToken()
  if (!accessToken) {
    throw new Error('Missing access token')
  }

  const response = await fetch(`${getBaseApiURL()}/conversations/${conversationId}/prompt/resume`, {
    method: 'POST',
    headers: {
      Accept: 'text/event-stream, application/json',
      Authorization: `Bearer ${accessToken}`,
    },
    signal,
  })

  await consumePromptResponse(response, onChunk, onEmergency, onDangerCheck)
}

/**
 * POST /anon/conversations/{id}/prompt and consume SSE text chunks (X-Anon-Session).
 */
export const streamAnonConversationPrompt = async ({
  conversationId,
  content,
  onChunk,
  onEmergency,
  onDangerCheck,
  signal,
}: TStreamConversationPromptParams): Promise<void> => {
  const response = await fetch(`${getBaseApiURL()}/anon/conversations/${conversationId}/prompt`, {
    method: 'POST',
    headers: {
      Accept: 'text/event-stream, application/json',
      'Content-Type': 'application/json',
      'X-Anon-Session': getAnonSessionId(),
    },
    body: JSON.stringify({ content }),
    signal,
  })

  await consumePromptResponse(response, onChunk, onEmergency, onDangerCheck)
}

/**
 * POST /anon/conversations/{id}/prompt/resume after answering No to the danger check.
 */
export const streamAnonConversationPromptResume = async ({
  conversationId,
  onChunk,
  onEmergency,
  onDangerCheck,
  signal,
}: TStreamConversationResumeParams): Promise<void> => {
  const response = await fetch(`${getBaseApiURL()}/anon/conversations/${conversationId}/prompt/resume`, {
    method: 'POST',
    headers: {
      Accept: 'text/event-stream, application/json',
      'X-Anon-Session': getAnonSessionId(),
    },
    signal,
  })

  await consumePromptResponse(response, onChunk, onEmergency, onDangerCheck)
}
